import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useToast } from './useToast';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(el => !el.hasAttribute('aria-hidden'));
}

/**
 * Bottom sheet. Traps focus while open, closes on Escape and on a backdrop
 * tap, restores focus to the opener, locks body scroll and drops the slide
 * animation under `prefers-reduced-motion`.
 */
export function Sheet({ open, onClose, title, children }: SheetProps) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { dismissToast } = useToast();

  // A toast raised before the sheet (the Undo toast) would float over it.
  useEffect(() => {
    if (open) {
      dismissToast();
    }
  }, [open, dismissToast]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    if (panel) {
      (getFocusable(panel)[0] ?? panel).focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) {
      return;
    }
    const focusable = getFocusable(panelRef.current);
    if (focusable.length === 0) {
      event.preventDefault();
      panelRef.current.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const fade = { duration: reduceMotion ? 0 : 0.2 };
  const slide = reduceMotion
    ? { duration: 0 }
    : { type: 'spring' as const, damping: 32, stiffness: 340 };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <motion.div
            data-testid="sheet-backdrop"
            aria-hidden="true"
            className="absolute inset-0 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
            onClick={() => onCloseRef.current()}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className="relative flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-bubble border-t border-white/10 bg-hc-sheet px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-card-lg focus:outline-none"
            initial={reduceMotion ? { opacity: 0 } : { y: '100%' }}
            animate={reduceMotion ? { opacity: 1 } : { y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { y: '100%' }}
            transition={slide}
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-white/20"
            />
            <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
              <h2
                id={titleId}
                className="min-w-0 truncate text-lg font-semibold text-white"
              >
                {title}
              </h2>
              <button
                type="button"
                onClick={() => onCloseRef.current()}
                aria-label={t('common.close')}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-accent"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
